import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import { isEmpty } from "lodash";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Modal } from "UI/Modal";
import {
  useGetGatewayCredentialsMutation,
  useGetInvoicingCredentialsMutation,
  usePostUpdateGatewayCredentialsMutation,
} from "api/baseAPI/apiCredentials";
import {
  useGetUsersMePointPermissionsQuery,
  useGetUsersSelfQuery,
} from "api/baseAPI/user";
import { Layout } from "components/layouts/Layout";
import { Box } from "UI/Box";
import { Button } from "UI/Button";
import { TextInput } from "UI/Form/TextInput";
import { Grid } from "UI/Grid";
import { Typography } from "UI/Typography";
import { getColors } from "utils/getColors";
import { ReactComponent as ArrowLeftIcon } from "utils/img/arrow-left.svg";
import { ReactComponent as LinkIcon } from "utils/img/link.svg";
import {
  useErrorNotification,
  useSuccessNotification,
} from "utils/notificationWrappers";
import { useSelectedPointManager } from "hooks/useSelectedEntity";
import { apiHost } from "api/baseQuery";
import { store } from "store";
import { RootState } from "reducers";
import { PointManagerItem } from "api/baseAPI/pointManagers";

const host = window.location.host.split(".");
host[0] = "docs";

const isSafari = () => {
  const ua = navigator.userAgent;
  const isChrome = /Chrome/.test(ua) && /Google Inc/.test(navigator.vendor);
  return /Safari/.test(ua) && !isChrome;
};

export const ApiCredentials = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const breadCrumbs = [
    [t("Управление"), ""],
    [t("Реквизиты"), ""],
  ];

  const { error } = getColors();
  const getUsersSelfQuery = useGetUsersSelfQuery();
  const selectedPointManager = useSelectedPointManager();
  const [getGatewayCredentials, getGatewayCredentialsMeta] =
    useGetGatewayCredentialsMutation();
  const [getInvoicingCredentials, getInvoicingCredentialsMeta] =
    useGetInvoicingCredentialsMutation();
  const [updateGatewayCredentials, updateGatewayCredentialsMeta] =
    usePostUpdateGatewayCredentialsMutation();
  const permissions = useGetUsersMePointPermissionsQuery();

  const form = useFormik({
    initialValues: {
      otp: "",
    },
    onSubmit: async (values) => {
      if (isSafari()) {
        const token = (store.getState() as RootState).auth.access_token;
        const lang = localStorage.getItem("lang");
        const pointManagerState = (store.getState() as RootState).basic
          .selectedPointManager;
        const pointManagerStore = window.localStorage.getItem("point_manager");
        const pointManager: PointManagerItem = pointManagerStore
          ? { ...JSON.parse(pointManagerStore) }
          : {};

        const headers = new Headers();

        headers.set("Content-Type", "application/json");
        if (token) {
          headers.set("Authorization", `Bearer ${token}`);
          headers.set("Accept-Language", lang ?? "uk");
          if (pointManager?.id || pointManagerState?.id) {
            headers.set(
              "Point-Manager",
              `${pointManager?.id || pointManagerState?.id}`
            );
          }
        }
        const text = new ClipboardItem({
          "text/plain": fetch(
            `${apiHost}/api-credentials/get-gateway-credentials/`,
            {
              method: "POST",
              body: JSON.stringify({
                point: selectedPointManager.point?.id || 0,
                otp: values.otp,
              }),
              headers,
            }
          )
            .then((resp) => resp.json())
            .then((resp) => {
              if (resp.api_token) {
                toast.success(t("Успех"));
                return new Blob([resp.api_token], { type: "text/plain" });
              }

              toast.error(t("Ошибка"));
              return new Blob([""], { type: "text/plain" });
            })
            .catch(() => {
              toast.error(t("Ошибка"));
              return new Blob([""], { type: "text/plain" });
            }),
        });

        navigator.clipboard.write([text]);
      } else {
        getGatewayCredentials({
          point: selectedPointManager.point?.id || 0,
          otp: values.otp,
        })
          .unwrap()
          .then((resp) => {
            form.setFieldTouched("otp", false);
            form.setFieldValue("otp", "");
            navigator.clipboard.writeText(resp.api_token);
          });
      }
    },
    validationSchema: Yup.object().shape({
      otp: Yup.string()
        .min(6, t("Значение этого поля должно состоять из 6 цифр"))
        .required(t("Обязательное поле")),
    }),
  });

  useEffect(() => {
    if (
      getInvoicingCredentialsMeta.isSuccess &&
      !isEmpty(getInvoicingCredentialsMeta.data)
    ) {
      navigator.clipboard.writeText(
        JSON.stringify(getInvoicingCredentialsMeta.data)
      );
    }
  }, [getInvoicingCredentialsMeta]);

  const handleCreateToken = () => {
    updateGatewayCredentials({
      otp: form.values.otp,
      point: selectedPointManager.point?.id || 0,
    })
      .unwrap()
      .then(() => {
        form.setFieldTouched("otp", false);
        form.setFieldValue("otp", "");
        setIsModalOpen(false);
      })
      .catch(() => {
        form.setFieldTouched("otp", false);
        form.setFieldValue("otp", "");
        setIsModalOpen(false);
      });
  };

  useSuccessNotification(
    [getGatewayCredentialsMeta],
    t("Токен скопирован в буфер обмена")
  );
  useSuccessNotification(
    [getInvoicingCredentialsMeta],
    t("Реквизиты скопированы в буфер обмена")
  );
  useSuccessNotification([updateGatewayCredentialsMeta]);
  useErrorNotification([getGatewayCredentialsMeta]);
  useErrorNotification([getInvoicingCredentialsMeta]);
  useErrorNotification([updateGatewayCredentialsMeta]);

  return (
    <Layout title={t("Реквизиты")} breadCrumbs={breadCrumbs}>
      <Grid container p={24} hSpace={24} vSpace={24}>
        <Grid item sm={12} xl={6}>
          <Box p={24} style={{ background: "white" }}>
            <Typography variant="h3" mb={12}>
              {t("Платежное API")}
            </Typography>
            <Typography variant="subtitle1">
              {t("Реквизиты, необходимые для интеграции с платежным API")}
            </Typography>
            <Box
              responsive={{
                xl: { mt: 32 },
                sm: { mt: 16 },
              }}
            >
              <TextInput
                size="small"
                label={t("Введите код OTP")}
                placeholder={t("Введите код")}
                onChange={(value) =>
                  form.setFieldValue(
                    "otp",
                    value.replace(/[^0-9]/g, "").slice(0, 6)
                  )
                }
                disabled={
                  getUsersSelfQuery.data && !getUsersSelfQuery.data?.otp_enabled
                }
                value={form.values.otp}
                error={form.touched.otp && Boolean(form.errors.otp)}
                helperText={form.touched.otp && form.errors.otp}
              />
              {getUsersSelfQuery.data && !getUsersSelfQuery.data?.otp_enabled && (
                <Typography style={{ display: "flex", color: error }} mt={8}>
                  {t("Получение токена без настроенного 2FA невозможно")}.
                  <Link to="/profile?otp=open">
                    <Typography ml={2} style={{ color: error }}>
                      {t("Настроить 2FA")}
                    </Typography>
                  </Link>
                </Typography>
              )}
            </Box>
            <Box
              flex
              responsive={{
                xl: { mt: 16, direction: "row" },
                sm: { mt: 8, direction: "column-reverse" },
              }}
              style={{ gap: "16px" }}
            >
              <Button
                variant="outlined"
                iconEnd={<LinkIcon />}
                disabled={
                  getGatewayCredentialsMeta.isLoading ||
                  !getUsersSelfQuery.data?.otp_enabled ||
                  !permissions.data?.includes("get_token")
                }
                onClick={() => form.handleSubmit()}
              >
                {t("Получить токен")}
              </Button>
              <Button
                variant="outlined"
                disabled={
                  getGatewayCredentialsMeta.isLoading ||
                  !getUsersSelfQuery.data?.otp_enabled ||
                  !permissions.data?.includes("update_token")
                }
                onClick={() => {
                  form.setFieldTouched("otp");
                  form.validateForm().then((value) => {
                    if (!value.otp) {
                      setIsModalOpen(true);
                    }
                  });
                }}
              >
                {t("Создать новый токен")}
              </Button>
              <a
                rel="noreferrer"
                href={`https://${host.join(".")}`}
                target="_blank"
                style={{ textDecoration: "none" }}
              >
                <Button
                  variant="text"
                  iconEnd={
                    <ArrowLeftIcon style={{ transform: "rotate(180deg)" }} />
                  }
                >
                  {t("Смотреть документацию")}
                </Button>
              </a>
            </Box>
          </Box>
        </Grid>
        <Grid item sm={12} xl={6}>
          <Box p={24} style={{ background: "white" }}>
            <Typography variant="h3" mb={12}>
              {t("API инвойсинга")}
            </Typography>
            <Typography variant="subtitle1">
              {t("Реквизиты, необходимые для интеграции с API инвойсинга")}
            </Typography>
            <Box
              flex
              responsive={{
                xl: { mt: 32, direction: "row" },
                sm: { mt: 16, direction: "column-reverse" },
              }}
            >
              <Button
                mt={8}
                variant="outlined"
                iconEnd={<LinkIcon />}
                disabled={getInvoicingCredentialsMeta.isLoading}
                onClick={() => getInvoicingCredentials()}
              >
                {t("Получить реквизиты")}
              </Button>
              <a
                rel="noreferrer"
                href={`https://${host.join(".")}`}
                target="_blank"
                style={{ textDecoration: "none" }}
              >
                <Button
                  mt={8}
                  variant="text"
                  iconEnd={
                    <ArrowLeftIcon style={{ transform: "rotate(180deg)" }} />
                  }
                  responsive={{
                    xl: { ml: 12 },
                    sm: { mt: 0 },
                  }}
                >
                  {t("Смотреть документацию")}
                </Button>
              </a>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Modal
        open={isModalOpen}
        hideHeader
        width={460}
        onClose={() => setIsModalOpen(false)}
      >
        <Box p={24}>
          <Typography variant="h5" mb={24} textAlign="center">
            {t("Создать новый токен?")}
          </Typography>
          <Typography variant="body" mb={8}>
            {t("После создания нового токена старый перестанет работать.")}
          </Typography>
          <Typography variant="body" mb={24}>
            {t(
              "Убедитесь, что вы обновите его в своих системах или интеграциях."
            )}
          </Typography>
          <Box flex alignItems="center" justifyContent="space-between">
            <Button variant="text" onClick={() => setIsModalOpen(false)}>
              {t("Отменить")}
            </Button>
            <Button
              variant="contained"
              disabled={updateGatewayCredentialsMeta.isLoading}
              onClick={handleCreateToken}
            >
              {t("Создать новый токен")}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Layout>
  );
};
